import { ref } from "vue";
import * as tf from "@tensorflow/tfjs";
import "@tensorflow/tfjs-backend-webgl";
import "@tensorflow/tfjs-backend-wasm";
import { setWasmPaths } from "@tensorflow/tfjs-backend-wasm";
import * as faceLandmarksDetection from "@tensorflow-models/face-landmarks-detection";

import {
  positionState,
  positionGetters,
  positionMutations,
  positionActions,
} from "./camera/position";
import {
  externalDeviceState,
  externalDeviceGetters,
  externalDeviceMutations,
  externalDeviceActions,
  _lastObjectDetectionTime,
} from "./camera/externalDevice";
import {
  personSwitchState,
  personSwitchGetters,
  personSwitchMutations,
  personSwitchActions,
} from "./camera/personSwitch";
import {
  multiplePersonState,
  multiplePersonGetters,
  multiplePersonMutations,
  multiplePersonActions,
} from "./camera/multiplePerson";
import {
  noiseState,
  noiseGetters,
  noiseMutations,
  noiseActions,
} from "./camera/noiseDetection";
import {
  faceVisibilityState,
  faceVisibilityGetters,
  faceVisibilityMutations,
  faceVisibilityActions,
} from "./camera/faceVisibility";
import {
  lookingAwayState,
  lookingAwayGetters,
  lookingAwayMutations,
  lookingAwayActions,
} from "./camera/lookingAway";

export let _cameraStream = null;
export let _screenStream = null;
let _detector = null;
let _verifyInterval = null;

export const _streamReady = ref(false);
export const _screenReady = ref(false);

export async function captureImageFromStream(stream) {
  if (!stream || !stream.getVideoTracks().length) return null;
  const track = stream.getVideoTracks()[0];
  if (window.ImageCapture) {
    try {
      const imageCapture = new ImageCapture(track);
      return await imageCapture.takePhoto();
    } catch (e) {
      console.warn("ImageCapture API failed", e);
    }
  }
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.srcObject = stream;
    video.play().catch(() => {});
    video.onloadeddata = () => {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          video.srcObject = null;
          resolve(blob);
        },
        "image/jpeg",
        0.8
      );
    };
    video.onerror = () => resolve(null);
  });
}

export const cameraState = {
  ...positionState,
  ...externalDeviceState,
  ...personSwitchState,
  ...multiplePersonState,
  ...noiseState,
  ...faceVisibilityState,
  ...lookingAwayState,
  animationId: null,
  lastDetectionTime: 0,
  hasCamera: false,
  hasMic: false,
  hasScreen: false,
  isTfReady: false,
  snapshotFrequencySeconds: 5,
  isPaperPenModeActive: false,
};

export const cameraGetters = {
  ...positionGetters,
  ...externalDeviceGetters,
  ...personSwitchGetters,
  ...multiplePersonGetters,
  ...noiseGetters,
  ...faceVisibilityGetters,
  ...lookingAwayGetters,
  cameraStream: () => {
    // eslint-disable-next-line no-unused-expressions
    _streamReady.value;
    return _cameraStream;
  },
  screenStream: () => {
    _screenReady.value;
    return _screenStream;
  },
  hasCamera: (state) => {
    _streamReady.value;
    return state.hasCamera && _cameraStream && _cameraStream.active;
  },
  hasMic: (state) => {
    _streamReady.value;
    return state.hasMic && _cameraStream && _cameraStream.active;
  },
  hasScreen: (state) => {
    _screenReady.value;
    return state.hasScreen && _screenStream && _screenStream.active;
  },
  isTfReady: (state) => state.isTfReady,
  canProceed: (state, getters) =>
    getters.hasCamera && getters.hasMic && getters.hasScreen,
};

export const cameraMutations = {
  ...positionMutations,
  ...externalDeviceMutations,
  ...personSwitchMutations,
  ...multiplePersonMutations,
  ...noiseMutations,
  ...faceVisibilityMutations,
  ...lookingAwayMutations,
  SET_ANIMATION_ID(state, id) {
    state.animationId = id;
  },
  SET_LAST_DETECTION_TIME(state, t) {
    state.lastDetectionTime = t;
  },
  SET_HAS_CAMERA(state, val) {
    state.hasCamera = val;
  },
  SET_HAS_MIC(state, val) {
    state.hasMic = val;
  },
  SET_HAS_SCREEN(state, val) {
    state.hasScreen = val;
  },
  SET_TF_READY(state, val) {
    state.isTfReady = val;
  },
  SET_CAMERA_STREAM(state, stream) {
    _cameraStream = stream;
    _streamReady.value = !!stream;
    state.hasCamera = !!stream;
    state.hasMic = !!stream;
  },
  SET_SCREEN_STREAM(state, stream) {
    _screenStream = stream;
    _screenReady.value = !!stream;
    state.hasScreen = !!stream;
  },
  SET_SNAPSHOT_FREQUENCY(state, val) {
    state.snapshotFrequencySeconds = val;
  },
  SET_PAPER_PEN_MODE_ACTIVE(state, val) {
    state.isPaperPenModeActive = val;
  },
};

export const cameraActions = {
  ...positionActions,
  ...externalDeviceActions,
  ...personSwitchActions,
  ...multiplePersonActions,
  ...noiseActions,
  ...faceVisibilityActions,
  ...lookingAwayActions,

  setPaperPenMode({ commit, dispatch }, active) {
    commit("SET_PAPER_PEN_MODE_ACTIVE", !!active);
    commit("SET_EYES_CLOSED_START", null);
    commit("SET_NO_FACE_START", null);
    dispatch(active ? "pausePersonSwitchWindow" : "resumePersonSwitchWindow");
  },

  async initTensorFlow({ commit, dispatch }) {
    try {
      const ua = (navigator.userAgent || "").toLowerCase();
      const isFirefox = ua.includes("firefox");

      setWasmPaths(
        "https://cdn.jsdelivr.net/npm/@tensorflow/tfjs-backend-wasm@4.22.0/dist/"
      );

      try {
        await tf.setBackend(isFirefox ? "wasm" : "webgl");
      } catch (e) {
        console.warn("[TF] Failed to set preferred backend, trying wasm:", e);
        await tf.setBackend("webgl");
      }
      await tf.ready();

      const model = faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
      _detector = await faceLandmarksDetection.createDetector(model, {
        runtime: "mediapipe",
        solutionPath: "https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh",
        maxFaces: 2,
      });

      await dispatch("initObjectDetector");
      await dispatch("initPersonSwitchModels");

      commit("SET_TF_READY", true);
    } catch (err) {
      console.error("TensorFlow init failed:", err);
    }
  },

  async initCamera({ commit, dispatch }) {
    if (_cameraStream && _cameraStream.active) {
      _streamReady.value = true;
      commit("SET_HAS_CAMERA", true);
      commit("SET_HAS_MIC", true);
      await dispatch("_publishCamera");
      return;
    }

    dispatch("suppressFocusViolations", { duration: 5000 });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720 },
        audio: true,
      });

      commit("SET_CAMERA_STREAM", stream);
      await dispatch("_publishCamera");
    } catch (err) {
      _cameraStream = null;
      _streamReady.value = false;
      commit("SET_HAS_CAMERA", false);
      commit("SET_HAS_MIC", false);

      if (err.name === "NotAllowedError") {
        throw {
          type: "camera_blocked",
          message:
            "Camera & microphone access was blocked. Please allow access in your browser settings and refresh.",
        };
      } else if (err.name === "NotFoundError") {
        throw {
          type: "camera_missing",
          message: "No camera found. Please connect a webcam and refresh.",
        };
      } else {
        throw {
          type: "camera_error",
          message: "Could not access camera: " + err.message,
        };
      }
    } finally {
      dispatch("suppressFocusViolations", { duration: 2000 });
    }
  },

  async requestScreenShare({ state, commit, dispatch }) {
    if (_screenStream && _screenStream.active) {
      commit("SET_HAS_SCREEN", true);
      await dispatch("_publishScreen");
      return { success: true };
    }

    dispatch("suppressFocusViolations", { duration: 10000 });
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: "monitor",
          frameRate: { ideal: 25, max: 25 },
        },
        audio: false,
      });

      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings();
      const surface = settings.displaySurface;
      const label = (track.label || "").toLowerCase();

      const isEntireScreen =
        surface === "monitor" ||
        (!surface &&
          (label.includes("screen") ||
            label.includes("display") ||
            label.includes("monitor") ||
            label.includes(":0") ||
            label.includes("entire")));

      if (!isEntireScreen) {
        stream.getTracks().forEach((t) => t.stop());
        commit("SET_HAS_SCREEN", false);
        return {
          success: false,
          message:
            "You selected a window or tab. You must share your Entire Screen. Please try again.",
        };
      }

      track.onended = async () => {
        if (state.isExamStarted) {
          commit("SET_SCREEN_SHARE_TERMINATED", true);
        } else {
          commit("SET_SCREEN_STREAM", null);
        }
      };

      commit("SET_SCREEN_STREAM", stream);
      commit("SET_SCREEN_SHARE_TERMINATED", false);
      await dispatch("_publishScreen");
      return { success: true };
    } catch (err) {
      commit("SET_SCREEN_STREAM", null);

      if (state.isExamStarted) {
        commit("SET_SCREEN_SHARE_TERMINATED", true);
      }

      if (err.name === "NotAllowedError") {
        return {
          success: false,
          message:
            "Screen sharing was cancelled. You must share your screen to take this exam.",
        };
      }
      return {
        success: false,
        message: err.message || "Screen sharing failed. Please try again.",
      };
    } finally {
      dispatch("suppressFocusViolations", { duration: 3000 });
    }
  },

  startDetection({ state, commit, dispatch }, videoEl) {
    const isScribe = localStorage.getItem("is_scribe") === "true";
    if (isScribe) return;

    if (state.animationId) {
      cancelAnimationFrame(state.animationId);
      commit("SET_ANIMATION_ID", null);
    }

    if (_verifyInterval) {
      clearInterval(_verifyInterval);
      _verifyInterval = null;
    }
    dispatch("startNoiseDetection");

    let lastVerifyTime = Date.now();

    _verifyInterval = setInterval(() => {
      if (state.isExamStarted) {
        const intervalMs = (state.snapshotFrequencySeconds || 5) * 1000;
        const now = Date.now();

        if (now - lastVerifyTime >= intervalMs) {
          lastVerifyTime = now;
        }
      } else {
        lastVerifyTime = Date.now();
      }
    }, 1000);

    let _lastFaceMeshTime = 0;
    let _lastFaceValidationTime = 0;
    let _lastPersonSwitchTime = 0;
    let _lastMultiplePersonTime = 0;
    const FACE_MESH_INTERVAL = 100;
    const FACE_VALIDATION_INTERVAL = 200;
    const OBJECT_DETECTION_INTERVAL = 500;
    const PERSON_SWITCH_INTERVAL = 500;
    const MULTIPLE_PERSON_INTERVAL = 500;

    let currentLoopId = null;

    const loop = async () => {
      if (currentLoopId !== state.animationId) return;

      const now = Date.now();

      if (
        !_detector ||
        !videoEl ||
        videoEl.readyState < 2 ||
        !videoEl.videoWidth ||
        !videoEl.videoHeight
      ) {
        currentLoopId = requestAnimationFrame(loop);
        commit("SET_ANIMATION_ID", currentLoopId);
        return;
      }

      try {
        let faces = null;
        if (now - _lastFaceMeshTime >= FACE_MESH_INTERVAL) {
          _lastFaceMeshTime = now;
          faces = await _detector.estimateFaces(videoEl);

          // 1a. No Face Detected (200ms)
          if (now - _lastFaceValidationTime >= FACE_VALIDATION_INTERVAL) {
            _lastFaceValidationTime = now;
            commit("SET_LAST_DETECTION_TIME", now);

            if (state.isPaperPenModeActive) {
              commit("SET_NO_FACE_START", null);
            } else if (!faces || faces.length === 0) {
              if (!state.noFaceStart) {
                commit("SET_NO_FACE_START", Date.now());
              } else {
                const elapsed = Date.now() - state.noFaceStart;
                if (elapsed > 120000) {
                  if (!state.maxViolationsReached) {
                    window.dispatchEvent(new CustomEvent("no-face-alert"));
                    // dispatch("createEvent", "TERMINATED").catch(() => {});
                    commit("SET_MAX_VIOLATIONS_REACHED", true);
                    dispatch("stopDetection");
                  }
                } else if (elapsed > 2000) {
                  dispatch("triggerViolation", "No face detected");
                }
              }
            } else {
              commit("SET_NO_FACE_START", null);
              if (faces.length === 1) {
                commit("SET_MULTIPLE_FACE_START", null);
              }
            }
          }
        }

        if (now - _lastMultiplePersonTime >= MULTIPLE_PERSON_INTERVAL) {
          _lastMultiplePersonTime = now;
          await dispatch("checkMultiplePersons", { videoEl });
        }

        // 3. Person Switch Detected (700ms) -- runs its own detection call
        if (
          !state.isPaperPenModeActive &&
          now - _lastPersonSwitchTime >= PERSON_SWITCH_INTERVAL
        ) {
          _lastPersonSwitchTime = now;
          await dispatch("checkPersonSwitch", { videoEl });
        }

        // Lower priority: head pose / looking-away, using this tick's
        // FaceMesh result (only available on ticks where FaceMesh ran).
        if (faces && faces.length === 1) {
          const landmarks = faces[0].keypoints;
          if (!state.isPaperPenModeActive) {
            dispatch("detectEyesClosed", landmarks);
          }
          dispatch("trackLipMovement", landmarks);
          if (state.activeSection !== "descriptive") {
            dispatch("checkFaceVisibility", {
              box: faces[0].box,
              landmarks,
              videoWidth: videoEl.videoWidth,
              videoHeight: videoEl.videoHeight,
            });
          }

          // Looking-away detection — independent module with 30s
          // continuous timer. Skipped in descriptive section.
          if (state.activeSection !== "descriptive") {
            dispatch("checkLookingAway", { landmarks });
          }
        }

        // Lowest priority: Phone / Object Detection (500ms)
        if (now - _lastObjectDetectionTime >= OBJECT_DETECTION_INTERVAL) {
          await dispatch("detectProhibitedObjects", videoEl);
        }
      } catch (err) {
        console.warn("Detection frame skipped:", err);
      }

      currentLoopId = requestAnimationFrame(loop);
      commit("SET_ANIMATION_ID", currentLoopId);
    };

    currentLoopId = requestAnimationFrame(loop);
    commit("SET_ANIMATION_ID", currentLoopId);
  },

  stopDetection({ state, commit }) {
    if (state.animationId) {
      cancelAnimationFrame(state.animationId);
      commit("SET_ANIMATION_ID", null);
    }
    if (_verifyInterval) {
      clearInterval(_verifyInterval);
      _verifyInterval = null;
    }
  },

  disconnectCameraStreams({ commit }) {
    if (_cameraStream) {
      _cameraStream.getTracks().forEach((t) => t.stop());
      commit("SET_CAMERA_STREAM", null);
    }
    if (_screenStream) {
      _screenStream.getTracks().forEach((t) => t.stop());
      commit("SET_SCREEN_STREAM", null);
    }
    _detector = null;
  },
};
